/**
 * Screen-time planner (owner idea 2026-11-04, ADR 0017). A grown-up sets minutes per day; the app
 * offers only content that fits in the time left, never stops something in the middle, and says
 * goodbye at the next natural break once nothing fits any more. Pure functions, no browser APIs.
 */

export interface ScreenTimeState {
  /** Minutes allowed per day; null = no limit. */
  limitMinutes: number | null;
  /** Local calendar day the usage belongs to (YYYY-MM-DD). */
  day: string;
  usedSeconds: number;
}

/** The local calendar day of a time (the planner resets at local midnight). */
export function localDay(now: Date): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Today's state: a new day starts at zero use with the same limit. */
export function forToday(state: ScreenTimeState | null, now: Date): ScreenTimeState {
  const day = localDay(now);
  if (!state) return { limitMinutes: null, day, usedSeconds: 0 };
  return state.day === day ? state : { limitMinutes: state.limitMinutes, day, usedSeconds: 0 };
}

/** Seconds left today (Infinity without a limit). */
export function secondsLeft(state: ScreenTimeState): number {
  if (state.limitMinutes === null) return Number.POSITIVE_INFINITY;
  return Math.max(0, state.limitMinutes * 60 - state.usedSeconds);
}

/** Whether something of this length can still start today. */
export const fitsToday = (state: ScreenTimeState, seconds: number) => seconds <= secondsLeft(state);

/**
 * Time to rest: a limit is set and nothing that could be started fits any more. `shortest` is the
 * shortest thing on offer (e.g. the shortest playable film); with nothing on offer, any time left
 * under a minute counts as used up.
 */
export function timeIsUp(state: ScreenTimeState, shortest: number | null): boolean {
  if (state.limitMinutes === null) return false;
  return secondsLeft(state) < (shortest ?? 60);
}

/** One more second of use (capped at a day). */
export const tick = (state: ScreenTimeState, seconds = 1): ScreenTimeState => ({
  ...state,
  usedSeconds: Math.min(86_400, state.usedSeconds + seconds),
});

/** Whole minutes left for display (rounded up, so "1 min" is shown until the very end). */
export const minutesLeft = (state: ScreenTimeState) => Math.ceil(secondsLeft(state) / 60);

export const LIMIT_CHOICES = [10, 20, 30, 45, 60] as const;
